import { PhotoCrosshair } from 'features/kestrelViewer/ui/tools/photoreel/components/PhotoCrosshair'
import { usePhotoUrl } from 'features/kestrelViewer/ui/tools/photoreel/hooks/usePhotoUrl'
import React, { useState } from 'react'
import styled from 'styled-components'
import { KVPhoto } from 'types'

const THUMBNAIL_SIZE_PX = 128 // 8rem = 128px

const ThumbnailContainer = styled.div`
  width: ${THUMBNAIL_SIZE_PX / 16}rem;
  height: ${THUMBNAIL_SIZE_PX / 16}rem;
  min-width: ${THUMBNAIL_SIZE_PX / 16}rem;
  min-height: ${THUMBNAIL_SIZE_PX / 16}rem;
  background-color: ${({ theme }) => theme.forecast.background2};
  border-radius: 0.5rem;
  overflow: hidden;
  transition: transform 0.2s ease;
  position: relative;

  &:hover {
    transform: scale(1.05);
  }
`

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: red;
  font-size: 12px;
  text-align: center;
  padding: 8px;
`

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`

interface PhotoReelThumbnailProps {
  photo: KVPhoto
  onClick: (id: string) => void
}

export const PhotoReelThumbnail: React.FC<PhotoReelThumbnailProps> = ({
  photo,
  onClick,
}) => {
  const [imageDimensions, setImageDimensions] = useState<{
    width: number
    height: number
  } | null>(null)

  const {
    url: thumbnailUrl,
    isLoading,
    error,
  } = usePhotoUrl(photo, 'thumbnail')

  const handleClick = () => {
    onClick(photo.id)
  }

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    setImageDimensions({
      width: img.naturalWidth,
      height: img.naturalHeight,
    })
  }

  const projection = photo.projection

  if (error) {
    return (
      <ThumbnailContainer onClick={handleClick}>
        <ErrorContainer>Error loading image</ErrorContainer>
      </ThumbnailContainer>
    )
  }

  return (
    <ThumbnailContainer onClick={handleClick}>
      {isLoading ? (
        <LoadingContainer>Loading...</LoadingContainer>
      ) : thumbnailUrl ? (
        <ThumbnailImage
          src={thumbnailUrl}
          alt='Photo thumbnail'
          onLoad={handleImageLoad}
        />
      ) : null}
      {projection && imageDimensions && !isLoading && (
        <PhotoCrosshair
          x={projection.x}
          y={projection.y}
          scale={0.6}
          containerWidth={THUMBNAIL_SIZE_PX}
          containerHeight={THUMBNAIL_SIZE_PX}
          imageWidth={imageDimensions.width}
          imageHeight={imageDimensions.height}
          originalImageWidth={projection.originalImageWidth}
          originalImageHeight={projection.originalImageHeight}
        />
      )}
    </ThumbnailContainer>
  )
}
